import { createFileRoute, Link, redirect, useLocation, useNavigate } from "@tanstack/react-router";
import { useGa4ProductView } from "@/hooks/useGa4ProductView";
import { metaLazy } from "@/lib/metaLazy";
import { getLegacyCapture, resolveLegacyRedirect, LIVE_PRODUCT_HANDLES } from "@/lib/legacy-redirects";
import { recordLegacyUrlVisit } from "@/lib/legacyUrlRegistry.functions";
import { quiet } from "@/lib/quiet-import";

import { useQuery, keepPreviousData } from "@tanstack/react-query";
import { CONTENT_DICTS } from "@/lib/i18n.content";
import * as React from "react";
import {
  ArrowLeft,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  CreditCard,
  Gift,
  Heart,
  Loader2,
  Phone,
  Play,
  Plus,
  Truck,
  ZoomIn,
} from "lucide-react";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";

import {
  fetchProductByHandle,
  fetchProducts,
  ShopifyMedia,
  ShopifyVariant,
} from "@/lib/shopify";
import { SiteShell } from "@/components/SiteShell";
import { PageHero } from "@/components/PageHero";
import { productHeroKey } from "@/utils/pageHeroImages.functions";
import { useIsMobile } from "@/hooks/use-mobile";
import { ShopAllFragrancesBar } from "@/components/ShopAllFragrancesBar";
import { CollectionTabsNav } from "@/components/CollectionTabsNav";
import { useLocale } from "@/contexts/LocaleContext";
import { useProductTranslations } from "@/hooks/useProductTranslations";
import { useCartStore } from "@/stores/cartStore";
import { Button } from "@/components/ui/button";
import { ProductCard } from "@/components/ProductCard";
import { GiftFlow } from "@/components/GiftFlow";
import { CopyLinkButton } from "@/components/CopyLinkButton";

import { PUBLIC_SITE_ORIGIN } from "@/lib/public-origin";
import { ImageLightbox } from "@/components/ImageLightbox";
import { MediaWithProgress } from "@/components/product/MediaWithProgress";
import { ARProductButton } from "@/components/product/ARProductButton";

import { useSwipe } from "@/hooks/useSwipe";
import { Skeleton } from "@/components/ui/skeleton";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { useAuth } from "@/contexts/AuthContext";
import { useWishlist } from "@/hooks/useWishlist";
import { toast } from "@/lib/toast";
import { cn } from "@/lib/utils";
import { DiscoveryKitBlock } from "@/components/DiscoveryKitBlock";
import { getProductDiscoveryKitBlock } from "@/utils/productDiscoveryKitBlock.functions";

import { ProductScentPyramid } from "@/components/product/ProductScentPyramid";
import {
  ProductStoryMedia,
  ProductStoryIntro,
  ProductStoryNote,
  ProductStoryBottomMedia,
} from "@/components/product/ProductStoryBlocks";
import { ProductReviews } from "@/components/product/ProductReviews";
import { RelatedProductsCarousel } from "@/components/product/RelatedProductsCarousel";
import { ProductLifestyleGrid } from "@/components/product/ProductLifestyleGrid";
import { ReadMoreText } from "@/components/product/ReadMoreText";

import { InlineBannerSlot } from "@/components/banners/BannerSlot";
import { productDeliveryPolicy } from "@/lib/storefront-cart-context";
import { LooxReviews } from "@/components/product/LooxReviews";

const WHATSAPP_NUMBER = "971508877701";
import { cachedSSR } from "@/lib/ssrCache";

const PHONE_NUMBER = "+971508877701";

import { publicCacheHeaders } from "@/lib/public-cache-headers";

export const Route = createFileRoute("/product/$handle")({
  headers: publicCacheHeaders,
  codeSplitGroupings: [["loader", "component", "notFoundComponent"]],
  // SSR loader so WhatsApp / social crawlers see real product metadata in HTML.
  // Uses AE as a neutral default market — bots have no locale context.
  loader: async ({ params }) => {
    // إعداد إظهار بلوك Discovery Kit (موبايل/ديسكتوب) — قراءة عامّة بلا auth
    // ⚡ TTFB: مخزَّن على السيرفر — لا استعلام قاعدة بيانات قبل أوّل بايت.
    const discoveryKit = await cachedSSR(
      "product-discovery-kit",
      async () => (await getProductDiscoveryKitBlock()).block,
      { showMobile: true, showDesktop: true },
    );
    try {
      const product = await fetchProductByHandle(params.handle, "AE");
      if (product) return { product, discoveryKit };
    } catch {
      /* fall through to the legacy-slug check below */
    }
    // A live product handle must NEVER redirect: a null/error here is a
    // transient fetch failure, and a cacheable redirect would permanently
    // bounce shoppers (and ads) to /shop. Render the not-found UI instead.
    if (LIVE_PRODUCT_HANDLES.has(params.handle.toLowerCase())) {
      return { product: null, discoveryKit };
    }
    // Unknown handle: the slug may be an old WooCommerce product URL that still
    // has ranking in Google. 301 it to its new home instead of showing a 404.
    const sourcePath = `/product/${params.handle}`;
    const capture = getLegacyCapture(sourcePath);
    if (capture) {
      const saved = await recordLegacyUrlVisit({ data: capture });
      if (saved.destinationPath) {
        throw redirect({ href: saved.destinationPath, statusCode: 301, reloadDocument: true });
      }
    }
    const legacy = resolveLegacyRedirect(sourcePath);
    if (legacy && legacy !== sourcePath) {
      // Mapped legacy products keep a permanent 301 (SEO transfer). The /shop
      // fallback for unknown slugs is a temporary 302 so a later real product
      // under this handle is never masked by a cached permanent redirect.
      const isMappedProduct = legacy.split("?")[0].startsWith("/product/");
      throw redirect({
        href: legacy,
        statusCode: isMappedProduct ? 301 : 302,
        reloadDocument: true,
      });
    }
    return { product: null, discoveryKit };
  },


  head: ({ loaderData, params }) => {
    const en = CONTENT_DICTS.en;
    const product = loaderData?.product;

    if (!product) {
      const title = en["home.meta.title"];
      const description = en["home.meta.description"];
      return {
        meta: [
          { title },
          { name: "description", content: description },
          { property: "og:title", content: title },
          { property: "og:description", content: description },
        ],
      };
    }

    // مقتطفات بحث مُنسَّقة للمنتجات عالية الظهور في جوجل؛ بقية المنتجات تبقى على السلوك الحالي.
    const seoTitle = en[`product.seo.${params.handle}.title`];
    const seoDesc = en[`product.seo.${params.handle}.description`];
    const baseTitle = seoTitle ?? `${product.title} | Lecmo Parfums`;
    const rawDesc = (product.description ?? "").replace(/\s+/g, " ").trim();
    const shortDesc =
      seoDesc ?? (rawDesc.length > 160 ? rawDesc.slice(0, 157) + "…" : rawDesc);
    const price = product.priceRange?.minVariantPrice;
    const priceLine = price ? ` — ${price.amount} ${price.currencyCode}` : "";
    const ogDesc = (shortDesc || en["home.meta.description"]) + priceLine;

    const rawImage = product.images?.edges?.[0]?.node?.url;
    // Shopify CDN supports `?width=` for on-the-fly resizing; 1200px is ideal for WhatsApp.
    const imageUrl = rawImage
      ? rawImage.includes("?")
        ? `${rawImage}&width=1200`
        : `${rawImage}?width=1200`
      : undefined;
    const imageAlt = product.images?.edges?.[0]?.node?.altText ?? product.title;

    const pageUrl = `${PUBLIC_SITE_ORIGIN}/product/${params.handle}`;

    const meta: Array<Record<string, string>> = [
      { title: baseTitle },
      { name: "description", content: shortDesc || en["home.meta.description"] },
      { property: "og:type", content: "product" },
      { property: "og:title", content: product.title },
      { property: "og:description", content: ogDesc },
      { property: "og:url", content: pageUrl },
      { property: "og:site_name", content: "Lecmo Parfums" },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: product.title },
      { name: "twitter:description", content: ogDesc },
    ];

    if (imageUrl) {
      meta.push(
        { property: "og:image", content: imageUrl },
        { property: "og:image:secure_url", content: imageUrl },
        { property: "og:image:width", content: "1200" },
        { property: "og:image:height", content: "1200" },
        { property: "og:image:alt", content: imageAlt },
        { name: "twitter:image", content: imageUrl },
      );
    }

    if (price) {
      meta.push(
        { property: "product:price:amount", content: price.amount },
        { property: "product:price:currency", content: price.currencyCode },
      );
    }

    // ── JSON-LD Product structured data ──
    const firstVariant = product.variants?.edges?.[0]?.node;
    const jsonLd: Record<string, unknown> = {
      "@context": "https://schema.org",
      "@type": "Product",
      name: product.title,
      description: shortDesc || undefined,
      url: pageUrl,
      brand: {
        "@type": "Brand",
        name: product.vendor || "Lecmo",
      },
    };
    if (imageUrl) jsonLd.image = imageUrl;
    if (price) {
      jsonLd.offers = {
        "@type": "Offer",
        url: pageUrl,
        priceCurrency: price.currencyCode,
        price: price.amount,
        availability: firstVariant?.availableForSale
          ? "https://schema.org/InStock"
          : "https://schema.org/OutOfStock",
        hasMerchantReturnPolicy: {
          "@id": `${PUBLIC_SITE_ORIGIN}/returns#policy`,
        },
        shippingDetails: {
          "@type": "OfferShippingDetails",
          hasShippingService: {
            "@id": `${PUBLIC_SITE_ORIGIN}/shipping#uae-shipping`,
          },
        },
        seller: { "@type": "Organization", name: "Lecmo Parfums" },
      };
    }

    // ── JSON-LD BreadcrumbList ──
    const breadcrumbLd = {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Lecmo", item: `${PUBLIC_SITE_ORIGIN}/` },
        { "@type": "ListItem", position: 2, name: "Shop", item: `${PUBLIC_SITE_ORIGIN}/shop` },
        { "@type": "ListItem", position: 3, name: product.title, item: pageUrl },
      ],
    };

    return {
      meta,
      links: [
        { rel: "canonical", href: pageUrl },
      ],
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify(jsonLd),
        },
        {
          type: "application/ld+json",
          children: JSON.stringify(breadcrumbLd),
        },
      ],
    };
  },
  component: ProductPage,
  notFoundComponent: () => (
    <SiteShell>
      <div className="mx-auto max-w-md py-40 text-center">
        <h1 className="t-h1">Fragrance not found</h1>
        <Link
          to="/shop"
          className="mt-6 inline-block t-caption uppercase tracking-[var(--tracking-wider)] text-foreground"
        >
          ← Back to shop
        </Link>
      </div>
    </SiteShell>
  ),
});

const SCENT_FALLBACK = {
  top: ["Bergamot", "Pink Pepper", "Saffron"],
  heart: ["Damask Rose", "Jasmine", "Iris"],
  base: ["Oud", "Amber", "Sandalwood"],
};

const SCENT_FALLBACK_AR = {
  top: ["برغموت", "فلفل وردي", "زعفران"],
  heart: ["وردة دمشقيّة", "ياسمين", "سوسن"],
  base: ["عود", "عنبر", "صندل"],
};

/**
 * يختار قيمة metafield بحسب اللغة مع fallback ذكيّ:
 * - locale=ar → ar ?? en ?? defaultValue
 * - locale=en → en ?? ar ?? defaultValue
 * يقبل قيم metafield كنصّ أو null/undefined.
 */
function pickLocalized(
  ar: string | null | undefined,
  en: string | null | undefined,
  locale: string,
  fallback: string,
): string {
  const a = (ar ?? "").trim();
  const e = (en ?? "").trim();
  if (locale.startsWith("ar")) return a || e || fallback;
  return e || a || fallback;
}

/**
 * يحلّل metafield notes إلى مصفوفة:
 * يقبل CSV ("ورد، ياسمين") أو سطور أو "·" أو "•".
 */
function parseNotesValue(raw: string | null | undefined): string[] | null {
  const s = (raw ?? "").trim();
  if (!s) return null;
  return s
    .split(/[,،\n·•|]/g)
    .map((x) => x.trim())
    .filter(Boolean);
}

type MediaItem = {
  kind: "image" | "video";
  src: string;
  preview?: string;
  alt?: string | null;
};

function ProductPage() {
  const { handle } = Route.useParams();
  const isMobileView = useIsMobile();
  const discoveryKitSettings = Route.useLoaderData()?.discoveryKit ?? {
    showMobile: true,
    showDesktop: true,
  };

  const {
    t,
    formatPrice,
    countryCode,
    locale,
    dir,
    effectiveCountryCode,
    effectiveMarket,
    marketId,
    marketReady,
  } = useLocale();
  const translations = useProductTranslations();
  const addItem = useCartStore((s) => s.addItem);
  const openCart = useCartStore((s) => s.openCart);
  const isLoading = useCartStore((s) => s.isLoading);
  const { user } = useAuth();
  const { has, add, remove, isPending: wishPending, shouldHintSignup, markSignupHintShown } = useWishlist();
  const navigate = useNavigate();
  const [giftOpen, setGiftOpen] = React.useState(false);
  const [addedRecently, setAddedRecently] = React.useState(false);
  const addTimeoutRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);
  // ملاحظة: زرّ المشاركة الكاملة بكود السفير انتقل إلى /account/media (المكتبة).
  // هنا فقط CopyLinkButton للزائر العاديّ + CTA توجيه للسفير المسجَّل.

  React.useEffect(() => {
    return () => {
      if (addTimeoutRef.current) clearTimeout(addTimeoutRef.current);
    };
  }, []);

  // Auto-open GiftFlow when navigated from agent
  React.useEffect(() => {
    if (typeof window === "undefined") return;
    const checkAndOpen = () => {
      const flag = sessionStorage.getItem("lecmo-agent-gift-open");
      console.log("[ProductPage] checkAndOpen, flag=", flag, "handle=", handle);
      if (flag === "1") {
        sessionStorage.removeItem("lecmo-agent-gift-open");
        console.log("[ProductPage] Opening GiftFlow from agent prefill");
        setGiftOpen(true);
      }
    };
    // Check on mount
    checkAndOpen();
    // Also check legacy ?gift=1 param
    const params = new URLSearchParams(window.location.search);
    if (params.get("gift") === "1") {
      setGiftOpen(true);
      const url = new URL(window.location.href);
      url.searchParams.delete("gift");
      window.history.replaceState({}, "", url.pathname);
    }
    // Listen for custom event (fires when user is already on this page)
    const handler = () => {
      // Small delay to ensure sessionStorage is written
      setTimeout(checkAndOpen, 100);
    };
    window.addEventListener("lecmo-gift-prefill", handler);
    return () => window.removeEventListener("lecmo-gift-prefill", handler);
  }, []);

  // ROW = لا شحن. نمنع «أضف للسلّة» لكن نُبقي «أرسل هديّة» (يفتح GiftFlow ويُحوّل لسوق المستلم).
  const isRowVisitor = marketId === "ROW" && effectiveMarket === "ROW";

  const {
    data: product,
    isLoading: loadingProduct,
    isFetching: fetchingProduct,
    isPlaceholderData: productIsPlaceholder,
  } = useQuery({
    queryKey: ["product", handle, effectiveCountryCode],
    queryFn: () => fetchProductByHandle(handle, effectiveCountryCode),
    placeholderData: keepPreviousData,
  });

  const { data: relatedProducts } = useQuery({
    queryKey: ["products", "related", effectiveCountryCode],
    queryFn: () => fetchProducts(8, effectiveCountryCode),
    placeholderData: keepPreviousData,
  });

  const [selectedVariant, setSelectedVariant] =
    React.useState<ShopifyVariant | null>(null);
  const [activeMedia, setActiveMedia] = React.useState(0);
  const [lightboxOpen, setLightboxOpen] = React.useState(false);
  const [giftLoading, setGiftLoading] = React.useState(false);
  const mediaCountRef = React.useRef(0);
  const googleViewOccurrence = React.useMemo(() => ({ id: product?.id }), [product?.id]);
  const heroSwipe = useSwipe({
    onNext: () => {
      const c = mediaCountRef.current;
      if (c > 1) setActiveMedia((i) => (i + 1) % c);
    },
    onPrev: () => {
      const c = mediaCountRef.current;
      if (c > 1) setActiveMedia((i) => (i - 1 + c) % c);
    },
    dir,
  });

  React.useEffect(() => {
    const firstVariant = product?.variants.edges[0]?.node;
    if (firstVariant) {
      setSelectedVariant(firstVariant);
    }
    setActiveMedia(0);
    // Meta/TikTok/OpenAI/Google retail view (GA4 view_item: useGa4ProductView)
    if (product) {
      const price = product.priceRange?.minVariantPrice;
      const viewPayload = {
        id: product.id,
        title: product.title,
        price: price?.amount ?? "0",
        currency: price?.currencyCode ?? "AED",
        tags: product.tags ?? [],
      };
      quiet(
        import("@/lib/googleDynamicRemarketing").then(({ trackGoogleRetailEvent }) =>
          trackGoogleRetailEvent(
            "view_item",
            {
              handle: product.handle,
              price: viewPayload.price,
              currency: viewPayload.currency,
            },
            googleViewOccurrence,
          ),
        ),
      );
      quiet(metaLazy(({ trackViewContent }) =>
        trackViewContent(viewPayload),
      ));
      quiet(import("@/lib/tiktokPixel").then(({ trackViewContent }) =>
        trackViewContent({
          productId: product.id,
          variantId: firstVariant?.id ?? null,
          title: viewPayload.title,
          price: viewPayload.price,
          currency: viewPayload.currency,
        }),
      ));
      quiet(import("@/lib/openaiAdsPixel").then(({ trackViewContent }) =>
        trackViewContent(viewPayload),
      ));
      quiet(import("@/lib/funnelClient").then(({ trackFunnelEvent }) =>
        trackFunnelEvent("checkout", "view_product", { productId: viewPayload.id }),
      ));

    }
  }, [product, googleViewOccurrence]);

  // Snap VIEW_CONTENT: one event per displayed variant snapshot (see snapViewForSelection).
  const snapViewKeyRef = React.useRef<string | null>(null);
  React.useEffect(() => {
    if (!product || !selectedVariant) return;
    const variantId = selectedVariant.id;
    quiet(
      import("@/lib/snapPixel").then(({ snapViewForSelection, trackViewContent }) => {
        const next = snapViewForSelection(product, variantId, snapViewKeyRef.current);
        if (!next) return;
        snapViewKeyRef.current = next.key;
        trackViewContent(next.payload);
      }),
    );
  }, [product, selectedVariant]);

  // GA4 view_item: one per route occurrence, from the displayed selection.
  const routePathname = useLocation({ select: (l) => l.pathname });
  useGa4ProductView({
    pathname: routePathname,
    handle,
    product,
    selectedVariant,
    marketReady,
    isPlaceholderData: productIsPlaceholder,
    country: effectiveCountryCode,
  });

  if (loadingProduct) {
    return (
      <SiteShell>
        <div className="mx-auto grid w-full max-w-6xl gap-12 px-4 pt-8 sm:px-6 md:grid-cols-[80px_1fr_1fr]">
          <Skeleton className="hidden md:block aspect-[1/4] w-full rounded-none" />
          <Skeleton className="mx-auto aspect-square w-[78%] max-w-[360px] rounded-none md:aspect-[3/4] md:w-full md:max-w-none" />
          <div className="space-y-4">
            <Skeleton className="h-8 w-3/4" />
            <Skeleton className="h-6 w-1/3" />
            <Skeleton className="h-32 w-full" />
          </div>
        </div>
      </SiteShell>
    );
  }

  if (!product) {
    return (
      <SiteShell>
        <div className="mx-auto max-w-md py-40 text-center">
          <h1 className="t-h1">Fragrance not found</h1>
          <Link
            to="/shop"
            className="mt-6 inline-block t-caption uppercase tracking-[var(--tracking-wider)] text-foreground"
          >
            ← Back to shop
          </Link>
        </div>
      </SiteShell>
    );
  }

  const images = product.images.edges.map((e) => e.node);
  const media: MediaItem[] = [];
  const seenMedia = new Set<string>();
  const pushMedia = (item: MediaItem) => {
    const key = `${item.kind}:${item.src}`;
    if (seenMedia.has(key)) return;
    seenMedia.add(key);
    media.push(item);
  };

  product.media?.edges.forEach((e) => {
    const node = e.node as ShopifyMedia;
    if ((node as any).mediaContentType === "VIDEO") {
      const v = node as any;
      const src =
        v.sources?.find((s: any) => s.format === "mp4")?.url ?? v.sources?.[0]?.url;
      if (src)
        pushMedia({
          kind: "video",
          src,
          preview: v.previewImage?.url,
          alt: v.alt,
        });
    } else if ((node as any).mediaContentType === "IMAGE") {
      const img = (node as any).image;
      if (img?.url) pushMedia({ kind: "image", src: img.url, alt: img.altText });
    }
  });
  images.forEach((img) => {
    if (img.url) pushMedia({ kind: "image", src: img.url, alt: img.altText });
  });
  mediaCountRef.current = media.length;

  // موديل ٣D للـAR Placement (يتطلّب رفع 3D model في Shopify Admin → المنتج → Media)
  const model3d = (product.media?.edges ?? []).find(
    (e) => (e.node as any).mediaContentType === "MODEL_3D",
  )?.node as
    | { sources: Array<{ url: string; mimeType: string; format: string }>; alt: string | null; previewImage?: { url: string } | null }
    | undefined;


  const onAdd = async () => {
    if (!selectedVariant || addedRecently) return;
    const ok = await addItem(
      {
        product: { node: product },
        variantId: selectedVariant.id,
        variantTitle: selectedVariant.title,
        price: selectedVariant.price,
        quantity: 1,
        selectedOptions: selectedVariant.selectedOptions ?? [],
      },
      effectiveCountryCode,
    );
    if (!ok) return;
    setAddedRecently(true);
    addTimeoutRef.current = setTimeout(() => {
      setAddedRecently(false);
      navigate({ to: "/cart" });
    }, 4000);
  };

  const onAddAsGift = () => {
    if (!selectedVariant || giftLoading || giftOpen) return;
    setGiftLoading(true);
    toast(t("cta.gift_preparing"), { duration: 1500, id: "gift-prep" });
    quiet(import("@/lib/ga4").then(({ trackGiftPreparing }) => trackGiftPreparing(product?.title)));
    quiet(metaLazy(({ trackGiftPreparing }) => trackGiftPreparing(product?.title)));
    setGiftOpen(true);
    requestAnimationFrame(() => setGiftLoading(false));
  };

  const isFav = product ? has(product.id) : false;
  const onToggleFav = async () => {
    if (!product) return;
    if (isFav) {
      await remove(product.id);
      return;
    }
    await add({
      node: {
        id: product.id,
        handle: product.handle,
        title: product.title,
        images: product.images,
        priceRange: product.priceRange,
        variants: { edges: selectedVariant ? [{ node: selectedVariant }] : [] },
      } as any,
    }, {
      variantIds: product.variants.edges.map((e) => e.node.id),
    });
    if (!user && shouldHintSignup()) {
      markSignupHintShown();
      toast(
        locale === "ar"
          ? "سجّل حسابك حتى لا تفقد مفضّلتك على أجهزة أخرى"
          : locale === "fr"
            ? "Créez un compte pour retrouver vos favoris partout"
            : "Sign up so you don't lose your favorites on other devices",
        {
          action: {
            label:
              locale === "ar" ? "تسجيل" : locale === "fr" ? "S'inscrire" : "Sign up",
            onClick: () =>
              navigate({ to: "/auth", search: { redirect: "/account/wishlist", mode: "signup" } }),
          },
          duration: 6000,
        },
      );
    }
  };

  const optionGroups = product.options.filter((o) => o.values.length > 1);
  const deliveryPolicy = productDeliveryPolicy(effectiveCountryCode);
  const sameDayLabel = t(deliveryPolicy.key, deliveryPolicy.vars);

  // ─── Localized content (metafields → fallback) ───
  // الأولويّة: metafield بلغة المستخدم → metafield بلغة أخرى → product.description (Shopify) → fallback i18n
  const localTr = translations?.[product.handle];
  // الاسم المحلَّى الكامل (يشمل النوع) — يُستخدم في الفتات والبلوكات
  const localizedFullTitle = pickLocalized(
    localTr?.titleAr || product.titleAr?.value,
    localTr?.titleEn || product.titleEn?.value,
    locale,
    product.title || "",
  );
  const editorContent = pickLocalized(
    product.editorNoteAr?.value,
    product.editorNoteEn?.value,
    locale,
    pickLocalized(
      localTr?.descriptionAr || product.descriptionAr?.value,
      localTr?.descriptionEn || product.descriptionEn?.value,
      locale,
      product.description ?? "",
    ),
  );
  const isAr = locale.startsWith("ar");
  const notesTop = parseNotesValue(isAr ? product.notesTopAr?.value : product.notesTopEn?.value)
    ?? parseNotesValue(isAr ? product.notesTopEn?.value : product.notesTopAr?.value)
    ?? parseNotesValue(isAr ? localTr?.notesTopAr : localTr?.notesTopEn)
    ?? parseNotesValue(isAr ? localTr?.notesTopEn : localTr?.notesTopAr)
    ?? (isAr ? SCENT_FALLBACK_AR.top : SCENT_FALLBACK.top);
  const notesHeart = parseNotesValue(isAr ? product.notesHeartAr?.value : product.notesHeartEn?.value)
    ?? parseNotesValue(isAr ? product.notesHeartEn?.value : product.notesHeartAr?.value)
    ?? parseNotesValue(isAr ? localTr?.notesMiddleAr : localTr?.notesMiddleEn)
    ?? parseNotesValue(isAr ? localTr?.notesMiddleEn : localTr?.notesMiddleAr)
    ?? (isAr ? SCENT_FALLBACK_AR.heart : SCENT_FALLBACK.heart);
  const notesBase = parseNotesValue(isAr ? product.notesBaseAr?.value : product.notesBaseEn?.value)
    ?? parseNotesValue(isAr ? product.notesBaseEn?.value : product.notesBaseAr?.value)
    ?? parseNotesValue(isAr ? localTr?.notesBaseAr : localTr?.notesBaseEn)
    ?? parseNotesValue(isAr ? localTr?.notesBaseEn : localTr?.notesBaseAr)
    ?? (isAr ? SCENT_FALLBACK_AR.base : SCENT_FALLBACK.base);

  // محتوى التبويبات/الأكورديون — مشترك
  const tabPanels = [
    {
      key: "editor",
      label: t("product.tab.editor"),
      content: editorContent ? (
        <ReadMoreText
          lines={1}
          readMoreLabel={t("product.read_more")}
          readLessLabel={t("product.read_less")}
        >
          {editorContent}
        </ReadMoreText>
      ) : (
        <p className="t-body-sm italic text-muted-foreground">—</p>
      ),
    },
    {
      key: "details",
      label: t("product.tab.details"),
      content: (
        <dl className="grid grid-cols-[auto_1fr] gap-x-6 gap-y-2 t-body-sm">
          {product.vendor && (
            <>
              <dt className="text-muted-foreground">{t("product.details_brand")}</dt>
              <dd className="text-foreground">
                {product.vendor === "LECMO PERFUME" ? "LECMO\u00a0" : product.vendor}
              </dd>
            </>
          )}
          {product.productType && (
            <>
              <dt className="text-muted-foreground">{t("product.details_type")}</dt>
              <dd className="text-foreground">{product.productType}</dd>
            </>
          )}
          {selectedVariant && (
            <>
              <dt className="text-muted-foreground">{t("product.details_variant")}</dt>
              <dd className="text-foreground">{selectedVariant.title}</dd>
            </>
          )}
        </dl>
      ),
    },
    {
      key: "notes",
      label: t("product.tab.notes"),
      content: (
        <Accordion type="single" collapsible defaultValue="top" className="w-full">
          {[
            { key: "top", label: t("product.top"), notes: notesTop },
            { key: "heart", label: t("product.heart"), notes: notesHeart },
            { key: "base", label: t("product.base"), notes: notesBase },
          ].map((row, idx) => (
            <AccordionItem
              key={row.key}
              value={row.key}
              className="border-b border-border/40 last:border-b-0"
            >
              <AccordionTrigger className="group py-4 hover:no-underline">
                <div className="flex w-full items-center gap-4">
                  <span className="t-micro w-6 tabular-nums text-muted-foreground">
                    0{idx + 1}
                  </span>
                  <span className="t-eyebrow uppercase tracking-[var(--tracking-wide)] text-foreground">
                    {row.label}
                  </span>
                </div>
              </AccordionTrigger>
              <AccordionContent className="pb-5 ps-10">
                <p className="font-serif t-body text-foreground md:t-body-lg">
                  {row.notes.join(" · ")}
                </p>
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      ),
    },
  ];


  const primaryCollectionHandle: string | undefined =
    (product as any)?.collections?.edges?.[0]?.node?.handle ?? undefined;

  const goPrev = () =>
    setActiveMedia((i) => (i - 1 + media.length) % media.length);
  const goNext = () => setActiveMedia((i) => (i + 1) % media.length);


  return (
    <SiteShell>
      {isMobileView ? (
        <PageHero
          pageKey={productHeroKey(handle)}
          fallbackPageKey="product"
          adminOnly
          headingAsDiv
          image=""
          alt={localizedFullTitle}
          title=""
          crumbLabel={localizedFullTitle}
        />
      ) : null}
      <CollectionTabsNav
        productCollectionHandles={
          (product as any)?.collections?.edges?.map(
            (e: { node: { handle: string } }) => e.node.handle,
          ) ?? []
        }
      />
      <div className="mx-auto w-full max-w-6xl px-4 pt-4 sm:px-6">
        <InlineBannerSlot placement="product_promo" />
      </div>
      <div className="mx-auto w-full max-w-6xl px-4 pb-20 sm:px-6 md:pb-24">
        <Link
          to="/shop"
          className="mb-6 inline-flex items-center gap-2 t-eyebrow uppercase tracking-[var(--tracking-wide)] text-muted-foreground transition-colors hover:text-foreground md:mb-8 md:text-xs"
        >
          <ArrowLeft className="h-4 w-4 rtl:-scale-x-100" /> {t("nav.shop")}
        </Link>

        <div className="grid gap-8 md:grid-cols-[80px_minmax(0,1fr)_minmax(0,1fr)] md:gap-8 lg:gap-10">
          {/* عمود المصغّرات العمودية — ديسكتوب فقط */}
          <div className="hidden md:flex flex-col gap-3">
            {media.map((m, i) => (
              <button
                key={i}
                onClick={() => setActiveMedia(i)}
                aria-label={`View media ${i + 1}`}
                className={cn(
                  "relative aspect-square w-full overflow-hidden rounded-none border bg-secondary transition-all",
                  activeMedia === i
                    ? "border-foreground opacity-100"
                    : "border-border/40 opacity-70 hover:opacity-100",
                )}
              >
                <img
                  src={m.kind === "video" ? m.preview ?? m.src : m.src}
                  alt=""
                  className="h-full w-full object-cover object-center"
                />
                {m.kind === "video" && (
                  <Play
                    className="pointer-events-none absolute inset-0 m-auto h-3 w-3 text-white drop-shadow"
                    fill="currentColor"
                  />
                )}
              </button>
            ))}
          </div>

          {/* الصورة الكبيرة */}
          <div className="min-w-0 space-y-3 md:space-y-4 -mx-4 sm:-mx-6 md:mx-0">
            <div
              className="relative aspect-square w-full select-none overflow-hidden rounded-none bg-secondary md:aspect-[3/4] md:w-full md:max-w-none md:rounded-none"
              onTouchStart={heroSwipe.onTouchStart}
              onTouchMove={heroSwipe.onTouchMove}
              onTouchEnd={heroSwipe.onTouchEnd}
            >
              <button
                type="button"
                onClick={() => setLightboxOpen(true)}
                aria-label={t("product.zoom_image") ?? "Zoom image"}
                className="absolute inset-0 z-[2] cursor-zoom-in"
              />
              <div className="pointer-events-none absolute end-3 bottom-3 z-[3] flex h-9 w-9 items-center justify-center bg-background/90 text-foreground backdrop-blur-sm md:end-4 md:bottom-4 md:h-10 md:w-10">
                <ZoomIn className="h-4 w-4 md:h-5 md:w-5" />
              </div>
              <div
                key={activeMedia}
                className="absolute inset-0 transition-opacity duration-200"
              >
                {media[activeMedia] ? (
                  <MediaWithProgress
                    kind={media[activeMedia].kind === "video" ? "video" : "image"}
                    src={media[activeMedia].src}
                    poster={media[activeMedia].preview}
                    alt={media[activeMedia].alt ?? product.title}
                    eager={activeMedia === 0}
                  />
                ) : null}
              </div>

              {/* أسهم تنقّل على الديسكتوب فقط */}
              {media.length > 1 && (
                <>
                  <button
                    onClick={goPrev}
                    aria-label="Previous image"
                    className="hidden md:inline-flex absolute start-3 top-1/2 z-[4] -translate-y-1/2 h-9 w-9 items-center justify-center rounded-none bg-background/85 backdrop-blur  text-foreground transition-colors hover:bg-background"
                  >
                    {dir === "rtl" ? (
                      <ChevronRight className="h-4 w-4" />
                    ) : (
                      <ChevronLeft className="h-4 w-4" />
                    )}
                  </button>
                  <button
                    onClick={goNext}
                    aria-label="Next image"
                    className="hidden md:inline-flex absolute end-3 top-1/2 z-[4] -translate-y-1/2 h-9 w-9 items-center justify-center rounded-none bg-background/85 backdrop-blur  text-foreground transition-colors hover:bg-background"
                  >
                    {dir === "rtl" ? (
                      <ChevronLeft className="h-4 w-4" />
                    ) : (
                      <ChevronRight className="h-4 w-4" />
                    )}
                  </button>
                </>
              )}
            </div>

            {/* مصغّرات أفقيّة — موبايل فقط */}
            {media.length > 1 && (
              <div className="md:hidden mx-auto flex w-full snap-x snap-mandatory items-center justify-start gap-2.5 overflow-x-auto px-4 sm:px-6 md:px-0 pb-1 [&::-webkit-scrollbar]:hidden">
                {media.map((m, i) => (
                  <button
                    key={i}
                    onClick={() => setActiveMedia(i)}
                    aria-label={`View media ${i + 1}`}
                    className={cn(
                      "relative h-16 w-16 shrink-0 snap-start overflow-hidden rounded-none border transition-all",
                      activeMedia === i
                        ? "border-foreground opacity-100"
                        : "border-border/40 opacity-70 hover:opacity-100",
                    )}
                  >
                    <img
                      src={m.kind === "video" ? m.preview ?? m.src : m.src}
                      alt=""
                      className="h-full w-full object-cover object-center"
                    />
                    {m.kind === "video" && (
                      <Play
                        className="pointer-events-none absolute inset-0 m-auto h-3 w-3 text-white drop-shadow"
                        fill="currentColor"
                      />
                    )}
                  </button>
                ))}
              </div>
            )}

            {/* زرّ AR — يظهر فقط إن رُفع موديل 3D في Shopify Admin → المنتج → Media */}
            {model3d && (
              <div className="mt-3 flex justify-center">
                <ARProductButton
                  model={model3d as any}
                  title={product.title}
                  poster={model3d.previewImage?.url ?? media[0]?.src}
                />
              </div>
            )}
          </div>


          {/* عمود التفاصيل */}
          <div className="md:sticky md:top-32 md:self-start">
            {/* Brand chip */}
            {product.vendor && (
              <p className="t-eyebrow-muted tracking-[var(--tracking-wider)]">
                {product.vendor}
              </p>
            )}

            <div className="mt-2 flex items-start justify-between gap-3">
              {(() => {
                const raw =
                  pickLocalized(
                    localTr?.titleAr || product.titleAr?.value,
                    localTr?.titleEn || product.titleEn?.value,
                    locale,
                    product.title || "",
                  );
                const sep = raw.includes(" - ") ? " - " : raw.includes(" – ") ? " – " : null;
                const name = sep ? raw.split(sep)[0].trim() : raw;
                const type = sep ? raw.slice(name.length + sep.length).trim() : "";
                return (
                  <div>
                    <h1 className="t-h1">{name}</h1>
                    {type && (
                      <p className="mt-1 font-sans text-[12px] uppercase tracking-[0.14em] text-muted-foreground md:text-[13px]">
                        {type}
                      </p>
                    )}
                  </div>
                );
              })()}

              <div className="flex shrink-0 items-center gap-2">
                <CopyLinkButton
                  url={`${PUBLIC_SITE_ORIGIN}/product/${handle}`}
                  ariaLabel={locale === "ar" ? "انسخ رابط المنتج" : "Copy product link"}
                  className="h-10 w-10 rounded-none border border-border hover:border-foreground"
                />
                <button
                  onClick={onToggleFav}
                  disabled={wishPending}
                  aria-label={isFav ? "Remove from wishlist" : "Add to wishlist"}
                  className={cn(
                    "inline-flex h-10 w-10 items-center justify-center rounded-none border transition-colors",
                    isFav
                      ? "border-foreground bg-foreground text-background"
                      : "border-border text-foreground hover:border-foreground",
                  )}
                >
                  <Heart className={cn("h-4 w-4", isFav && "fill-current")} />
                </button>
              </div>
            </div>

            {selectedVariant && (
              <>
                <p
                  className={cn(
                    "mt-3 t-h2 t-tabular text-foreground transition-opacity duration-200",
                    fetchingProduct && "opacity-60",
                  )}
                  aria-busy={fetchingProduct}
                  aria-live="polite"
                >
                  {formatPrice(
                    selectedVariant.price.amount,
                    selectedVariant.price.currencyCode,
                  )}
                  {fetchingProduct && (
                    <Loader2 className="inline-block ms-2 h-3.5 w-3.5 animate-spin text-muted-foreground align-middle" />
                  )}
                </p>
                {isRowVisitor && (
                  <p className="mt-1 t-eyebrow normal-case tracking-normal italic text-muted-foreground">
                    {t("row.price_approx_notice")}
                  </p>
                )}
                {/* بانر Tabby — لا يظهر لزوّار ROW (لا شحن، لا دفع) */}
                {!isRowVisitor && deliveryPolicy.installments && (
                  <p className="mt-2 inline-flex items-center gap-1.5 t-eyebrow normal-case tracking-normal text-muted-foreground">
                    <CreditCard className="h-3.5 w-3.5 text-foreground" />
                    <span>
                      {t("product.tabby_banner", {
                        amount: formatPrice(
                          (parseFloat(selectedVariant.price.amount) / 4).toFixed(2),
                          selectedVariant.price.currencyCode,
                        ),
                      })}
                    </span>
                  </p>
                )}
              </>
            )}

            {/* Shipping bar */}
            <div className="mt-5 flex items-start gap-3 border-t border-b border-border py-4 t-body-sm">
              <Truck className="mt-0.5 h-4 w-4 shrink-0 text-foreground" />
              <div className="space-y-0.5">
                <p className="font-medium text-foreground">
                  {t("product.shipping_from")}
                </p>
                <p className="text-muted-foreground">{sameDayLabel}</p>
              </div>
            </div>

            {/* Variant options */}
            {optionGroups.length > 0 && selectedVariant && (
              <div className="mt-6 space-y-5">
                {optionGroups.map((opt) => (
                  <div key={opt.name}>
                    <p className="mb-2 t-eyebrow-muted">
                      {opt.name}
                    </p>
                    <div className="flex flex-wrap gap-2">
                      {opt.values.map((val) => {
                        const isActive =
                          selectedVariant.selectedOptions.find(
                            (s) => s.name === opt.name,
                          )?.value === val;
                        return (
                          <button
                            key={val}
                            onClick={() => {
                              const next = product.variants.edges.find((e) =>
                                e.node.selectedOptions.every((s) =>
                                  s.name === opt.name
                                    ? s.value === val
                                    : selectedVariant.selectedOptions.find(
                                        (cs) => cs.name === s.name,
                                      )?.value === s.value,
                                ),
                              );
                              if (next) setSelectedVariant(next.node);
                            }}
                            className={cn(
                              "min-h-11 border px-4 py-2 t-eyebrow uppercase tracking-[var(--tracking-wide)] transition-colors",
                              isActive
                                ? "border-foreground bg-foreground text-background"
                                : "border-border text-foreground hover:border-foreground",
                            )}
                          >
                            {val}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* CTAs */}
            {isRowVisitor ? (
              <div className="mt-6 space-y-3 md:mt-8">
                <div className="rounded-none border border-amber-500/40 bg-amber-50/60 p-3 t-caption text-amber-900 dark:bg-amber-950/30 dark:text-amber-200">
                  <p className="font-medium">{t("row.not_serving_country")}</p>
                  <p className="mt-1 t-eyebrow normal-case tracking-normal opacity-90">
                    {t("row.gift_alternative")}
                  </p>
                </div>
                <div className="flex flex-col gap-2.5 sm:flex-row">
                  <a
                    href={`https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(
                      t("product.whatsapp_inquiry", {
                        title: product.title,
                        url: typeof window !== "undefined" ? `${window.location.origin}/product/${product.handle}` : `/product/${product.handle}`,
                      }),
                    )}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex h-12 flex-1 items-center justify-center gap-2 rounded-none bg-foreground t-caption font-medium uppercase tracking-[var(--tracking-wide)] text-background transition-colors hover:bg-foreground/90 md:h-14"
                  >
                    <svg viewBox="0 0 24 24" className="h-4 w-4" fill="currentColor" aria-hidden>
                      <path d="M.057 24l1.687-6.163a11.867 11.867 0 0 1-1.587-5.946C.16 5.335 5.495 0 12.05 0a11.817 11.817 0 0 1 8.413 3.488 11.824 11.824 0 0 1 3.48 8.414c-.003 6.557-5.338 11.892-11.893 11.892a11.9 11.9 0 0 1-5.688-1.448L.057 24zm6.597-3.807c1.676.995 3.276 1.591 5.392 1.592 5.448 0 9.886-4.434 9.889-9.885.002-5.462-4.415-9.89-9.881-9.892-5.452 0-9.887 4.434-9.889 9.884-.001 2.225.651 3.891 1.746 5.634l-.999 3.648 3.742-.981zm11.387-5.464c-.074-.124-.272-.198-.57-.347-.297-.149-1.758-.868-2.031-.967-.272-.099-.47-.149-.669.149-.198.297-.768.967-.941 1.165-.173.198-.347.223-.644.074-.297-.149-1.255-.462-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.297-.347.446-.521.151-.172.2-.296.3-.495.099-.198.05-.371-.025-.52-.075-.149-.669-1.611-.916-2.206-.242-.579-.487-.5-.669-.51l-.57-.01c-.198 0-.52.074-.792.372s-1.04 1.016-1.04 2.479 1.065 2.876 1.213 3.074c.149.198 2.095 3.2 5.076 4.487.709.306 1.263.489 1.694.626.712.226 1.36.194 1.872.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413z" />
                    </svg>
                    {t("row.contact_whatsapp")}
                  </a>
                   {selectedVariant?.availableForSale === false ? (
                     <TooltipProvider delayDuration={200}>
                       <Tooltip>
                         <TooltipTrigger asChild>
                            <span className="flex-1" tabIndex={0} onClick={() => {
                              quiet(import("@/lib/ga4").then(({ trackGiftOosClick }) => trackGiftOosClick(product!.title, selectedVariant?.title)));
                              quiet(metaLazy(({ trackGiftOosClick }) => trackGiftOosClick(product!.title, selectedVariant?.title)));
                            }}>
                             <Button
                               disabled
                               variant="outline"
                               size="lg"
                               className="h-12 w-full gap-2 rounded-none border-foreground t-caption font-medium uppercase tracking-[var(--tracking-wide)] text-foreground md:h-14"
                             >
                               <Gift className="h-4 w-4" />
                               {t("cta.gift")}
                             </Button>
                           </span>
                         </TooltipTrigger>
                         <TooltipContent side="top"><p>{t("cta.gift_oos")}</p></TooltipContent>
                       </Tooltip>
                     </TooltipProvider>
                   ) : (
                     <Button
                       onClick={onAddAsGift}
                       variant="outline"
                       size="lg"
                       className="h-12 flex-1 gap-2 rounded-none border-foreground t-caption font-medium uppercase tracking-[var(--tracking-wide)] text-foreground hover:bg-foreground hover:text-background md:h-14"
                      >
                        {giftLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Gift className="h-4 w-4" />}
                        {t("cta.gift")}
                      </Button>
                   )}
                </div>
              </div>
            ) : (
              <div className="mt-6 flex flex-col items-center justify-center gap-2.5 sm:flex-row md:mt-8">
                {addedRecently ? (
                  <div className="flex h-14 w-full max-w-md items-center justify-center gap-2 rounded-none border border-foreground/30 bg-background t-caption font-medium uppercase tracking-[var(--tracking-wide)] text-foreground md:h-16 md:max-w-lg">
                    <Loader2 className="h-4 w-4 animate-spin" />
                    {t("product.added_to_cart")}
                  </div>
                ) : (
                  <Button
                    onClick={onAdd}
                    disabled={isLoading || !marketReady || !selectedVariant?.availableForSale}
                    size="lg"
                    className="h-14 w-full max-w-md gap-2 rounded-none bg-foreground t-caption font-medium uppercase tracking-[var(--tracking-wide)] text-background hover:bg-foreground/90 md:h-16 md:max-w-lg"
                  >
                    {isLoading || !marketReady ? (
                      <>
                        <Loader2 className="h-4 w-4 animate-spin" />
                        {!marketReady && !isLoading ? t("cart.detecting_country") : null}
                      </>
                    ) : (
                      <>
                        <Plus className="h-4 w-4" />
                        {t("cta.add")}
                      </>
                    )}
                  </Button>
                )}
                {selectedVariant?.availableForSale === false ? (
                  <TooltipProvider delayDuration={200}>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <span className="flex-1" tabIndex={0} onClick={() => {
                          quiet(import("@/lib/ga4").then(({ trackGiftOosClick }) => trackGiftOosClick(product!.title, selectedVariant?.title)));
                          quiet(metaLazy(({ trackGiftOosClick }) => trackGiftOosClick(product!.title, selectedVariant?.title)));
                        }}>
                          <Button
                            disabled
                            variant="outline"
                            size="lg"
                            className="h-12 w-full gap-2 rounded-none border-foreground t-caption font-medium uppercase tracking-[var(--tracking-wide)] text-foreground md:h-14"
                          >
                            <Gift className="h-4 w-4" />
                            {t("cta.gift")}
                          </Button>
                        </span>
                      </TooltipTrigger>
                      <TooltipContent side="top"><p>{t("cta.gift_oos")}</p></TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                ) : (
                  <Button
                    onClick={onAddAsGift}
                    variant="outline"
                    size="lg"
                    className="h-14 w-full max-w-md gap-2 rounded-none border-foreground t-caption font-medium uppercase tracking-[var(--tracking-wide)] text-foreground hover:bg-foreground hover:text-background md:h-16 md:max-w-lg"
                    >
                      {giftLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Gift className="h-4 w-4" />}
                      {t("cta.gift")}
                    </Button>
                )}
              </div>
            )}



            {/* بلوك Discovery Kit — موبايل فقط داخل عمود التفاصيل */}
            {discoveryKitSettings.showMobile && (
              <div className="mt-6 md:hidden">
                <DiscoveryKitBlock
                  collectionHandle={primaryCollectionHandle}
                  initialHandle={product.handle}
                />
              </div>
            )}



            {/* Tabs — أفقيّة بنفس الشكل على الموبايل والديسكتوب */}
            <div className="mt-8">
              <DesktopTabs panels={tabPanels} />
            </div>


          </div>
        </div>

        {discoveryKitSettings.showDesktop && (
          <>
            {/* فاصل بعنوان مركزيّ بين البلوكين — ديسكتوب وموبايل */}
            <div className="border-y border-rule-accent bg-background">
              <div className="flex min-h-[56px] items-center justify-center px-4 text-center md:min-h-[72px]">
                <h2 className="text-[11px] uppercase tracking-[0.24em] text-foreground md:text-[13px]">
                  {locale === "ar" ? "طقم الاكتشاف" : "Discovery Kit"}
                </h2>
              </div>
            </div>

            {/* بلوك Discovery Kit — ديسكتوب فقط تحت شبكة المنتج */}
            <div className="hidden md:block">
              <DiscoveryKitBlock
                collectionHandle={primaryCollectionHandle}
                initialHandle={product.handle}
              />
            </div>
          </>
        )}
        {/* نوتات العطر — مباشرةً تحت بلوك المنتج */}
        <ProductScentPyramid
          handle={product.handle}
          className="mt-10 md:mt-14"
          notesOverride={{
            top: notesTop.join(" · "),
            heart: notesHeart.join(" · "),
            base: notesBase.join(" · "),
          }}
        />

        {/* بلوك نصّي وسطيّ (اختياريّ) — أعلى بلوك الوسائط */}
        <ProductStoryIntro handle={product.handle} productTitle={localizedFullTitle} />

        {/* بلوك وسائط (صورة/فيديو) لكلّ منتج — /admin/product-story-blocks */}
        <ProductStoryMedia handle={product.handle} alt={product.title} />

        {/* بلوك نصّ تحريريّ يأخذ اسم العطر عنواناً */}
        <ProductStoryNote handle={product.handle} productTitle={localizedFullTitle} />

        {/* بلوك وسائط (صورة/فيديو) أسفل النصّ التحريريّ — /admin/product-story-blocks */}
        <ProductStoryBottomMedia handle={product.handle} alt={product.title} />


        {/* ٣ صور Lifestyle لكلّ منتج — /admin/product-lifestyle */}
        <ProductLifestyleGrid handle={product.handle} />





        {/* Related */}
        {relatedProducts && relatedProducts.length > 0 && (
          <section className="mt-12 border-y border-foreground bg-background md:mt-16">
            {/* Header row — بنفس نمط تبويبات الهوم (Nemro): UPPERCASE + tracking + ارتفاع 64/94 */}
            <div className="flex min-h-[64px] items-center justify-center border-b border-foreground px-4 md:min-h-[94px] md:px-7">
              <h2 className="text-center font-sans text-[11px] uppercase tracking-[0.16em] text-foreground md:text-[13px]">
                {t("product.pairs")}
              </h2>
            </div>
            <RelatedProductsCarousel
              items={relatedProducts
                .filter((p) => p.node.handle !== handle)
                .slice(0, 8)}
            />
          </section>
        )}

        {/* Reviews */}
        <ProductReviews handle={product.handle} />

        {/* Need help bar — أسفل كل شيء قبل الفوتر */}
        <div className="mt-8 flex flex-wrap items-center justify-center gap-x-8 gap-y-3 border-t border-border/60 pt-8 t-caption md:mt-12">
          <span className="text-muted-foreground">
            {t("product.need_help")}
          </span>
          <a
            href={`tel:${PHONE_NUMBER}`}
            className="inline-flex items-center gap-1.5 font-medium uppercase tracking-[var(--tracking-wide)] text-foreground hover:underline"
          >
            <Phone className="h-3.5 w-3.5" />
            {t("product.call_us")}
          </a>
          <a
            href={`https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(
              t("product.whatsapp_inquiry", {
                title: product.title,
                url: typeof window !== "undefined" ? `${window.location.origin}/product/${product.handle}` : `/product/${product.handle}`,
              }),
            )}`}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1.5 font-medium uppercase tracking-[var(--tracking-wide)] text-foreground hover:underline"
          >
            <svg
              viewBox="0 0 24 24"
              className="h-3.5 w-3.5"
              fill="currentColor"
              aria-hidden
            >
              <path d="M.057 24l1.687-6.163a11.867 11.867 0 0 1-1.587-5.946C.16 5.335 5.495 0 12.05 0a11.817 11.817 0 0 1 8.413 3.488 11.824 11.824 0 0 1 3.48 8.414c-.003 6.557-5.338 11.892-11.893 11.892a11.9 11.9 0 0 1-5.688-1.448L.057 24zm6.597-3.807c1.676.995 3.276 1.591 5.392 1.592 5.448 0 9.886-4.434 9.889-9.885.002-5.462-4.415-9.89-9.881-9.892-5.452 0-9.887 4.434-9.889 9.884-.001 2.225.651 3.891 1.746 5.634l-.999 3.648 3.742-.981zm11.387-5.464c-.074-.124-.272-.198-.57-.347-.297-.149-1.758-.868-2.031-.967-.272-.099-.47-.149-.669.149-.198.297-.768.967-.941 1.165-.173.198-.347.223-.644.074-.297-.149-1.255-.462-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.297-.347.446-.521.151-.172.2-.296.3-.495.099-.198.05-.371-.025-.52-.075-.149-.669-1.611-.916-2.206-.242-.579-.487-.5-.669-.51l-.57-.01c-.198 0-.52.074-.792.372s-1.04 1.016-1.04 2.479 1.065 2.876 1.213 3.074c.149.198 2.095 3.2 5.076 4.487.709.306 1.263.489 1.694.626.712.226 1.36.194 1.872.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413z" />
            </svg>
            {t("product.whatsapp")}
          </a>
        </div>
      </div>

      {selectedVariant && (
        <GiftFlow
          open={giftOpen}
          onClose={() => setGiftOpen(false)}
          product={{ node: product }}
          variantId={selectedVariant.id}
          variantTitle={selectedVariant.title}
          price={selectedVariant.price}
          selectedOptions={selectedVariant.selectedOptions ?? []}
        />
      )}

      

      {lightboxOpen && (
        <ImageLightbox
          media={media}
          index={activeMedia}
          onIndexChange={setActiveMedia}
          onClose={() => setLightboxOpen(false)}
          title={product.title}
          description={product.description ?? undefined}
        />
      )}
      <div className="flex min-h-[92px] items-center justify-center border-t border-foreground bg-background md:min-h-[118px]">
        <button
          type="button"
          onClick={() => {
            if (typeof window !== "undefined") {
              window.scrollTo({ top: 0, behavior: "smooth" });
            }
          }}
          aria-label={locale === "ar" ? "العودة إلى الأعلى" : "Back to top"}
          className="group inline-flex items-center gap-4 border-b border-foreground pb-2 text-[11px] uppercase tracking-[0.16em] text-foreground transition-opacity hover:opacity-70"
        >
          {locale === "ar" ? "العودة إلى الأعلى" : "Back to top"}
          <span
            aria-hidden
            className="relative inline-block h-3 w-3 transition-transform group-hover:-translate-y-1"
          >
            <span className="absolute left-[1px] top-[1px] h-2 w-2 border-t border-l border-current" />
            <span
              className="absolute left-[2px] top-[2px] h-[11px] border-l border-current origin-top"
            />
          </span>
        </button>
      </div>

    </SiteShell>
  );
}

function DesktopTabs({
  panels,
}: {
  panels: Array<{ key: string; label: string; content: React.ReactNode }>;
}) {
  const [active, setActive] = React.useState(panels[0]?.key);
  const current = panels.find((p) => p.key === active) ?? panels[0];
  return (
    <div>
      <div className="flex flex-nowrap items-center gap-x-5 overflow-x-auto whitespace-nowrap border-b border-border/60 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
        {panels.map((p) => (
          <button
            key={p.key}
            onClick={() => setActive(p.key)}
            className={cn(
              "relative -mb-px shrink-0 pb-3 t-caption uppercase tracking-[var(--tracking-wide)] transition-colors",
              active === p.key
                ? "text-foreground"
                : "text-muted-foreground hover:text-foreground",
            )}
          >
            {p.label}
            {active === p.key && (
              <span className="absolute inset-x-0 -bottom-px h-[2px] bg-foreground" />
            )}
          </button>
        ))}
      </div>
      <div className="pt-5">{current?.content}</div>
    </div>
  );
}

function MobileSection({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <Collapsible className="border-b border-border/60">
      <CollapsibleTrigger className="group flex h-12 w-full items-center justify-between t-eyebrow uppercase tracking-[var(--tracking-wide)] text-foreground">
        <span>{label}</span>
        <ChevronDown className="h-4 w-4 text-foreground transition-transform duration-200 group-data-[state=open]:rotate-180" />
      </CollapsibleTrigger>
      <CollapsibleContent className="pb-5">{children}</CollapsibleContent>
    </Collapsible>
  );
}

function NoteRow({ label, notes }: { label: string; notes: string[] }) {
  return (
    <div className="grid grid-cols-[72px_1fr] gap-3 border-b border-border/40 pb-3 md:grid-cols-[100px_1fr] md:gap-4">
      <span className="self-center t-micro uppercase tracking-[var(--tracking-wide)] text-muted-foreground">
        {label}
      </span>
      <span className="font-serif t-body text-foreground md:t-body-lg">
        {notes.join(" · ")}
      </span>
    </div>
  );
}
